"use client";

// Frontière d'erreur générale — tout ce qui n'a pas de frontière plus proche :
// réglages, éditeurs plein écran, pages publiques, tunnel d'authentification.
//
// La coquille applicative a la sienne (`(protected)/(app-shell)/error.tsx`) et
// garde sa barre latérale ; ici il n'y a pas de chrome à préserver, donc la
// page est autonome et centrée.
//
// Avant ce fichier, le dépôt n'avait AUCUNE frontière d'erreur côté serveur :
// n'importe quelle erreur de rendu tombait sur la page d'erreur brute de Next,
// sans un mot en français ni le moindre chemin de sortie.

import * as React from "react";
import Link from "next/link";
import * as Sentry from "@sentry/nextjs";
import { AlertTriangle, RotateCcw, Home, LogIn } from "lucide-react";

import { Button } from "@/components/ui/button";

export default function AppError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  React.useEffect(() => {
    // Cf. `(protected)/(app-shell)/error.tsx` : une erreur serveur porte un
    // `digest` et a déjà été remontée par `onRequestError`. On ne signale donc
    // que les erreurs client, que personne d'autre ne voit.
    if (!error.digest) Sentry.captureException(error);
  }, [error]);

  return (
    <main className="flex min-h-screen items-center justify-center bg-background p-6">
      <div className="w-full max-w-md rounded-2xl border border-neutral-100 bg-white p-7 text-center shadow-sm">
        <div className="mx-auto flex size-12 items-center justify-center rounded-2xl bg-warning-surface text-warning">
          <AlertTriangle className="size-5" />
        </div>

        <h1 className="mt-4 text-h4 text-neutral-900">Quelque chose s&apos;est mal passé</h1>
        <p className="mt-2 text-body-sm text-neutral-500">
          Cette page n&apos;a pas pu s&apos;afficher. Réessayer suffit le plus souvent.
          Si vous étiez inactif un moment, votre session a peut-être simplement
          expiré — reconnectez-vous et reprenez où vous en étiez.
        </p>

        <div className="mt-5 flex flex-col items-center gap-2 sm:flex-row sm:justify-center">
          <Button onClick={reset}>
            <RotateCcw />
            Réessayer
          </Button>
          <Button variant="secondary" asChild>
            <Link href="/">
              <Home />
              Accueil
            </Link>
          </Button>
          <Button variant="ghost" asChild>
            <Link href="/auth/login">
              <LogIn />
              Se reconnecter
            </Link>
          </Button>
        </div>

        {error.digest ? (
          <p className="mt-5 text-caption text-neutral-400">
            Référence : <span className="font-mono">{error.digest}</span>
          </p>
        ) : null}
      </div>
    </main>
  );
}
